/**
 * Shared vehicle RFID sync utility.
 * Single source of truth for syncing / revoking a vehicle's RFID tag to all
 * authorized devices (dedicated RFID readers + Guarita integrations).
 *
 * Rules:
 *  - device_type === 'rfid_reader' → sync via DeviceIntegration
 *  - device_type === 'guarita'      → register / delete card on Guarita TCP API
 *  - everything else (facial_reader, camera, …) → skip for vehicle RFID sync
 */

import api from '../services/api';
import { fetchWithAuth } from '../services/api';
import { DeviceInfo, DeviceOperations, CommunicationMode, apiDeviceToDeviceInfo } from '../services/deviceCommunication';
import { DeviceIntegration } from '../integrations/devices';
import { registerOnDeviceWithMask, deleteFromDevice, listGuaritaIntegrations } from '../services/guaritaIntegrationService';
import { syncPlateToLprCameras, describeLprSyncFailures } from '../services/lprPlateSync';
import { normalizePlate } from '../services/hikvisionLprPlates';
import { tagValueToDecimal } from './tagValueRadix';
import toast from 'react-hot-toast';
import { reportDeviceSyncOutcome } from './deviceSyncOutcome';

// ── helpers ─────────────────────────────────────────────────────────────────

/** Convert any RFID tag value to the Guarita decimal serial string. */
export function rfidToGuaritaDecimal(tag: string): string | null {
  const t = (tag || '').trim();
  if (!t) return null;
  let dec: number;
  if (t.includes(',')) {
    const [f, c] = t.split(',');
    const fc = parseInt(f.trim(), 10);
    const cn = parseInt(c?.trim() ?? '', 10);
    if (isNaN(fc) || isNaN(cn)) return null;
    // A real Wiegand26 facility code is only 8 bits (0-255) — a bigger value means this
    // isn't actually Wiegand26 (most likely a ControlID 40-bit UHF tag's raw string reused
    // as-is) and combining it produces an inflated value the Guarita TP (vehicle) slot's
    // real 24-bit hardware cap always rejects. See guaritaSerial.ts for the full writeup.
    if (fc < 0 || fc > 0xFF) return null;
    dec = fc * 65536 + cn;
  } else {
    // 0x prefix, an A-F digit, or a leading zero all mean hex — the reader's own padding,
    // and the same rule the tag entry forms record with (see utils/tagValueRadix). Reading
    // a padded "0989390" as decimal 989390 here registered the wrong card on the Guarita
    // while ControlID, which does honour the padding, registered the right one.
    const parsed = tagValueToDecimal(t, 'dec');
    if (parsed === null) return null;
    dec = parsed;
  }
  if (isNaN(dec) || dec <= 0) return null;
  return String(dec);
}

// ── zone device lookup ───────────────────────────────────────────────────────

export interface PersonDeviceIds {
  rfidIds: string[];
  guaritaIntegrationIds: string[];
  /**
   * hikvision_lpr_cameras row ids for DS-TCG406 LPR cameras in the person's zones.
   *
   * An LPR camera holds its own plate allow-list, so it is a vehicle sync target exactly like an
   * RFID reader — it just receives the plate instead of the tag. The camera is attached to a zone
   * as an iot_device whose config carries the id of its hikvision_lpr_cameras row, mirroring how
   * guarita devices carry guarita_integration_id.
   */
  lprCameraIds: string[];
  /**
   * LPR devices found in the zones whose camera link could not be resolved.
   *
   * Kept separate from lprCameraIds so the caller can say "there is a camera here that I could
   * not write to" instead of reporting success for the RFID readers alone — the silent-drop
   * behaviour that made a zone's LPR camera look like it was never configured.
   */
  unresolvedLprDevices: string[];
}

/** Parse zone device lists into rfidIds + guaritaIntegrationIds (with guarita_integration_id lookup). */
async function parseZoneDevices(customerId: string, zones: any[]): Promise<PersonDeviceIds> {
  const rfidIds = new Set<string>();
  const guaritaIntegrationIds = new Set<string>();
  const guaritaDeviceIdsToFetch: string[] = [];
  const lprCameraIds = new Set<string>();
  const lprDeviceIdsToFetch: Array<{ id: string; name: string }> = [];

  zones.forEach(z => {
    // A zone can be an explicit "deny" profile — the backend documents "Deny profiles
    // always override allow" (access_profile_handler.go). Registering/keeping a vehicle's
    // tag on a device inside a zone the vehicle is denied from would grant physical access
    // the system's own access-control rules say it shouldn't have.
    if ((z.access_type || '').toLowerCase() === 'deny') return;
    z.devices?.forEach((d: any) => {
      const t = (d.device_type || '').toLowerCase();
      if (t === 'rfid_reader' || t === 'controlid_rfid') {
        rfidIds.add(d.id);
      } else if (t === 'guarita') {
        if (d.config?.guarita_integration_id) {
          guaritaIntegrationIds.add(d.config.guarita_integration_id);
        } else {
          guaritaDeviceIdsToFetch.push(d.id);
        }
      } else if (t === 'hikvision_lpr' || t === 'lpr') {
        // Both device_types reach here on purpose. The device form has a generic "Tipo: LPR"
        // tab (device_type 'lpr') as well as the Hikvision-specific create path
        // ('hikvision_lpr'), and a camera saved through the generic form comes back as 'lpr'.
        // Matching only 'hikvision_lpr' silently excluded real cameras from plate sync.
        //
        // config.hikvision_lpr_camera_id is what actually identifies the camera, and keying on
        // it keeps alphadigi (which carries alphadigi_camera_id) correctly excluded.
        if (d.config?.hikvision_lpr_camera_id) {
          lprCameraIds.add(d.config.hikvision_lpr_camera_id);
        } else {
          lprDeviceIdsToFetch.push({ id: d.id, name: d.name || 'Câmera LPR' });
        }
      }
      // facial_reader, camera, sensor, qr_scanner → skip.
      // alphadigi_lpr is skipped too: that camera has no plate allow-list to sync into, it
      // only reports reads (see alphadigiCameraService).
    });
  });

  if (guaritaDeviceIdsToFetch.length > 0) {
    await Promise.all(guaritaDeviceIdsToFetch.map(async (id) => {
      try {
        const r = await api.get(`/iot-devices/${id}`);
        if (r.data.config?.guarita_integration_id) {
          guaritaIntegrationIds.add(r.data.config.guarita_integration_id);
        }
      } catch { /* ignored */ }
    }));
  }

  // Resolution fallback for zone payloads that predate `config` on EffectiveDevice (and for
  // any device whose camera link is genuinely missing).
  //
  // This used to swallow every failure, which made an LPR camera vanish from the sync with no
  // error and no log: the plate was never written, and the toast still reported success for the
  // RFID readers alone. An LPR device we cannot resolve is now reported, not dropped.
  const unresolvedLprDevices: string[] = [];
  if (lprDeviceIdsToFetch.length > 0) {
    await Promise.all(lprDeviceIdsToFetch.map(async ({ id, name }) => {
      try {
        const r = await api.get(`/iot-devices/${id}`);
        const cameraId = r.data?.config?.hikvision_lpr_camera_id;
        if (cameraId) {
          lprCameraIds.add(cameraId);
        } else {
          unresolvedLprDevices.push(`${name} (sem câmera vinculada)`);
        }
      } catch (e: any) {
        unresolvedLprDevices.push(`${name} (${e?.message || 'falha ao consultar o dispositivo'})`);
      }
    }));
  }

  // Which zones were considered and what was found in them. A vehicle that syncs to its RFID
  // reader but not to a camera at the same gate is almost always an access-profile fact (the
  // camera's zone is not in this person's effective zones) rather than a sync failure, and
  // without this the two cases are indistinguishable from the UI.
  console.log('[Vehicle Sync] zone device resolution:', {
    zones: zones.map(z => ({
      name: z.name,
      access: z.access_type,
      devices: (z.devices || []).map((d: any) => `${d.device_type}:${d.name || d.id}`),
    })),
    resolved: {
      rfid: rfidIds.size,
      guarita: guaritaIntegrationIds.size,
      lprCameras: Array.from(lprCameraIds),
      lprUnresolved: unresolvedLprDevices,
    },
  });

  return {
    rfidIds: Array.from(rfidIds),
    guaritaIntegrationIds: Array.from(guaritaIntegrationIds),
    lprCameraIds: Array.from(lprCameraIds),
    unresolvedLprDevices,
  };
}

/**
 * Fetch effective-access-zones for a person and return:
 *  - rfidIds: IoT device IDs with device_type = 'rfid_reader'
 *  - guaritaIntegrationIds: Guarita integration UUIDs from guarita devices
 */
export async function fetchPersonVehicleDeviceIds(
  customerId: string,
  personId: string,
): Promise<PersonDeviceIds> {
  try {
    const res = await api.get(`/customers/${customerId}/people/${personId}/effective-access-zones`);
    return parseZoneDevices(customerId, res.data.zones || []);
  } catch {
    return { rfidIds: [], guaritaIntegrationIds: [], lprCameraIds: [], unresolvedLprDevices: [] };
  }
}

/**
 * Fetch effective-access-zones for a unit (via unit_profile_assignments) and return device IDs.
 * Used as fallback when a vehicle is linked to a unit but the unit has no morador.
 */
export async function fetchUnitVehicleDeviceIds(
  customerId: string,
  unitId: string,
): Promise<PersonDeviceIds> {
  try {
    const res = await api.get(`/customers/${customerId}/units/${unitId}/effective-access-zones`);
    return parseZoneDevices(customerId, res.data.zones || []);
  } catch {
    return { rfidIds: [], guaritaIntegrationIds: [], lprCameraIds: [], unresolvedLprDevices: [] };
  }
}

// ── resident lookup ──────────────────────────────────────────────────────────

/** For unit-linked vehicles (no person_id), return the first active resident's id. */
export async function resolveVehiclePersonId(
  customerId: string,
  vehicle: { person_id?: string | null; unit_id?: string | null },
): Promise<string | null> {
  if (vehicle.person_id) return vehicle.person_id;
  if (!vehicle.unit_id) return null;
  try {
    const res = await fetchWithAuth(
      `/api/v1/customers/${customerId}/people?role_type=resident&unit_id=${vehicle.unit_id}&page_size=1`,
    );
    if (res.ok) {
      const data = await res.json();
      const residents = data.data || data.people || [];
      if (residents.length > 0) return residents[0].id;
    }
  } catch { /* ignored */ }
  return null;
}

// ── sync ─────────────────────────────────────────────────────────────────────

export interface SyncVehicleOptions {
  customerId: string;
  vehicle: {
    id: string;
    plate?: string;
    rfid_tag?: string | null;
    person_id?: string | null;
    unit_id?: string | null;
    person?: { first_name?: string; last_name?: string } | null;
    owner_name?: string;
  };
  isAllowed: boolean; // true = sync, false = revoke
}

/**
 * Sync or revoke a vehicle's RFID tag on all authorized RFID readers + Guarita.
 * Shows toasts internally.
 * Returns true if at least one device/integration succeeded.
 */
export async function syncVehicleRfid({ customerId, vehicle, isAllowed }: SyncVehicleOptions): Promise<boolean> {
  const personId = await resolveVehiclePersonId(customerId, vehicle);
  // The ledger is keyed by person; a unit-linked vehicle resolves to its resident.
  const tagPersonId = personId;

  let rfidIds: string[];
  let guaritaIntegrationIds: string[];
  let lprCameraIds: string[];
  let unresolvedLprDevices: string[];

  if (personId) {
    ({ rfidIds, guaritaIntegrationIds, lprCameraIds, unresolvedLprDevices } =
      await fetchPersonVehicleDeviceIds(customerId, personId));
  } else if (vehicle.unit_id) {
    // No morador — resolve devices via unit_profile_assignments directly
    ({ rfidIds, guaritaIntegrationIds, lprCameraIds, unresolvedLprDevices } =
      await fetchUnitVehicleDeviceIds(customerId, vehicle.unit_id));
  } else {
    toast.error('Veículo sem proprietário ou unidade — não é possível sincronizar');
    return false;
  }

  // Respect the per-integration "vehicle_whitelist_enabled" toggle (ZonesDevices.tsx —
  // "Permite sincronizar tags RFID de veículos ativos nesta Guarita"). Without this check,
  // the per-vehicle Sync/Revoke buttons silently registered/deleted tags on a Guarita
  // device even when an admin had explicitly disabled vehicle sync for it.
  if (guaritaIntegrationIds.length > 0) {
    try {
      const integrations = await listGuaritaIntegrations(customerId);
      const enabledIds = new Set(
        integrations.filter(i => i.vehicle_whitelist_enabled).map(i => i.id),
      );
      guaritaIntegrationIds = guaritaIntegrationIds.filter(id => enabledIds.has(id));
    } catch {
      // If the integrations list can't be fetched, fail closed for Guarita (skip it)
      // rather than risk syncing to a device whose whitelist state is unknown.
      guaritaIntegrationIds = [];
    }
  }

  // LPR cameras are keyed by plate, not by RFID tag — a vehicle without a plate has nothing
  // to sync to them even when its zones contain cameras.
  if (!vehicle.plate) lprCameraIds = [];

  // An LPR camera in the zones that we could not address is a gate this vehicle will not open.
  // Say so: reporting only the RFID result here is what made the camera look unconfigured.
  if (vehicle.plate && unresolvedLprDevices.length > 0) {
    toast.error(`Câmera LPR não sincronizada — ${unresolvedLprDevices.join('; ')}`);
  }

  if (rfidIds.length === 0 && guaritaIntegrationIds.length === 0 && lprCameraIds.length === 0) {
    toast('Nenhum dispositivo RFID autorizado encontrado');
    return false;
  }

  let anyOk = false;
  // Tracks whether ANY target refused the tag. The returned flag means "fully synced":
  // it used to mean "something worked", so a caller writing `if (ok)` read a tag that
  // reached one reader out of two as a clean sync.
  let anyFailed = false;

  // ── Hikvision LPR cameras ─────────────────────────────────────────────────
  // The camera keeps its own allow-list, so the plate is written to it the way a tag is written
  // to an RFID reader. Runs before the tag paths because a vehicle may reach a gate by plate
  // only, with no tag at all.
  if (lprCameraIds.length > 0) {
    const ownerName = vehicle.person
      ? `${vehicle.person.first_name || ''} ${vehicle.person.last_name || ''}`.trim()
      : (vehicle.owner_name || '');

    const lprResult = await syncPlateToLprCameras(
      customerId,
      lprCameraIds,
      { plate: vehicle.plate as string, ownerName },
      isAllowed,
    );

    if (lprResult.ok.length > 0) anyOk = true;

    // Partial failures must be named, never rounded up into the overall success toast.
    const lprWarning = describeLprSyncFailures(lprResult);
    if (lprWarning) toast.error(lprWarning);
  }
  const plate = vehicle.plate || vehicle.rfid_tag || vehicle.id;
  const verb = isAllowed ? 'Sincronizando' : 'Revogando';

  // ── Guarita ───────────────────────────────────────────────────────────────
  if (guaritaIntegrationIds.length > 0) {
    // Guarita is a real target here. If the tag cannot be written to it, say so EVERY
    // time — not only when guarita is the sole target. Skipping it quietly because an
    // RFID reader happened to succeed leaves the car unable to open the guarita gate
    // with nothing on screen explaining why.
    if (!vehicle.rfid_tag) {
      toast.error('Veículo sem tag RFID — não é possível sincronizar na Guarita');
      if (rfidIds.length === 0) return false;
    } else {
      const cardDecimal = rfidToGuaritaDecimal(vehicle.rfid_tag);
      if (!cardDecimal) {
        toast.error('Tag RFID inválida para Guarita');
        if (rfidIds.length === 0) return false;
      } else {
        const toastId = `sync-guarita-${vehicle.id}`;
        toast.loading(`${verb} ${plate} na guarita...`, { id: toastId });
        let guaritaOk = 0;
        for (const integrationId of guaritaIntegrationIds) {
          // The ledger is keyed by (person, device, credential_type); a vehicle tag is a
          // card, and the module it was written to is the device.
          const reportGuarita = (success: boolean, errorMessage?: string) => {
            if (!tagPersonId) return;
            reportDeviceSyncOutcome({
              customerId,
              personId: tagPersonId,
              deviceId: integrationId,
              credentialType: 'rfid_card',
              success,
              source: 'cloud',
              errorMessage,
            });
          };

          try {
            if (isAllowed) {
              const label = plate.substring(0, 18);
              await registerOnDeviceWithMask(integrationId, cardDecimal, label, 0xFF, 6); // 6 = DeviceTypeTP (passive UHF tag)
            } else {
              await deleteFromDevice(integrationId, cardDecimal, 6); // 6 = DeviceTypeTP (passive UHF tag)
            }
            guaritaOk++;
            // Only a GRANT confirms a credential present; a revoke is reported by the
            // revoke funnel, never as a grant, or the ledger would resurrect it.
            if (isAllowed) reportGuarita(true);
          } catch (e: any) {
            if (e.message?.includes('já cadastrada')) {
              guaritaOk++; // already on device = ok
              if (isAllowed) reportGuarita(true);
            } else {
              anyFailed = true;
              toast.error(e.message || 'Erro ao sincronizar tag na Guarita');
              if (isAllowed) reportGuarita(false, e?.message || 'Erro ao sincronizar tag na Guarita');
            }
          }
        }
        toast.dismiss(toastId);
        if (guaritaOk > 0) {
          anyOk = true;
          if (rfidIds.length === 0) {
            toast.success(`${plate} ${isAllowed ? 'sincronizado' : 'revogado'} na guarita`);
            return !anyFailed;
          }
        }
      }
    }
  }

  // ── Standard RFID readers ─────────────────────────────────────────────────
  if (rfidIds.length === 0) return anyOk && !anyFailed;


  const isElectronApp = typeof window !== 'undefined' && !!(window as any).electronAPI;

  if (isElectronApp) {
    const deviceDetails = await Promise.all(
      rfidIds.map(async (deviceId) => {
        try {
          const r = await api.get(`/iot-devices/${deviceId}`);
          return apiDeviceToDeviceInfo(r.data);
        } catch { return null; }
      }),
    );
    const validDevices = deviceDetails.filter((d): d is DeviceInfo => d !== null);
    if (validDevices.length > 0) {
      const hasDirectDevices = validDevices.some(d => !!(d.local_ip_address || d.ip_address));
      if (hasDirectDevices) {
        toast.loading(`${verb} RFID ${plate} diretamente...`, { id: `sync-rfid-${vehicle.id}` });
      }
      const personSyncData = {
        person_id: vehicle.id,
        first_name: plate,
        last_name: vehicle.person
          ? `${vehicle.person.first_name || ''} ${vehicle.person.last_name || ''}`.trim()
          : (vehicle.owner_name || ''),
        identity_photo_url: undefined,
        isAllowed,
        rfid_tags: vehicle.rfid_tag ? [vehicle.rfid_tag] : [],
        vehicle_plate: vehicle.plate || '',
      };
      const results = await Promise.all(
        validDevices.map(d =>
          DeviceIntegration.instance.execute(d, DeviceOperations.SYNC_PERSON, personSyncData, CommunicationMode.HYBRID),
        ),
      );
      toast.dismiss(`sync-rfid-${vehicle.id}`);

      // Every reader's outcome reaches the ledger, successes AND failures — a tag that
      // did not land is precisely what reconciliation needs to know about.
      if (tagPersonId && isAllowed) {
        results.forEach((r, i) => {
          reportDeviceSyncOutcome({
            customerId,
            personId: tagPersonId,
            deviceId: validDevices[i].id,
            device: validDevices[i],
            credentialType: 'rfid_card',
            success: !!r.success,
            source: r.source,
            durationMs: r.latencyMs,
            errorMessage: r.success ? undefined : (r.error || 'Falha ao gravar a tag'),
          });
        });
      }

      const successes = results.filter(r => r.success);
      if (successes.length > 0) {
        anyOk = true;
        const localCount = successes.filter(r => r.source === 'local').length;
        const publicCount = successes.filter(r => r.source === 'public').length;
        const parts: string[] = [];
        if (localCount > 0) parts.push(`${localCount} local`);
        if (publicCount > 0) parts.push(`${publicCount} público`);
        toast.success(
          `RFID ${plate} ${isAllowed ? 'sincronizado' : 'revogado'} com ${successes.length} leitor(es)` +
          (parts.length > 0 ? ` (${parts.join(', ')})` : ''),
        );

        // Readers that refused the tag must be NAMED. Announcing only the successes
        // turned a half-written tag into a clean-looking sync, which is how a car ends up
        // opening one gate and not the next.
        const failures = results.filter(r => !r.success);
        if (failures.length > 0) {
          anyFailed = true;
          const reasons = Array.from(new Set(failures.map(r => r.error).filter(Boolean)));
          toast.error(
            `${failures.length} leitor(es) não gravaram a tag ${plate}` +
            (reasons.length > 0 ? `: ${reasons.join('; ')}` : ''),
            { duration: 7000 },
          );
        }
        return !anyFailed;
      }
      if (hasDirectDevices) toast.error('Dispositivos inacessíveis diretamente — verifique a rede');
    }
  } else {
    // Browser: cloud-only path
    let cloudOk = 0;
    for (const deviceId of rfidIds) {
      try {
        await api.post(`/iot-devices/${deviceId}/sync`);
        cloudOk++;
      } catch { /* continue */ }
    }
    if (cloudOk > 0) {
      anyOk = true;
      toast.success(`${plate} ${isAllowed ? 'sincronizado' : 'revogado'} via nuvem (${cloudOk} leitor(es))`);
    } else {
      toast(`Sincronização enviada para ${plate}`);
    }
  }

  return anyOk && !anyFailed;
}

/**
 * Removes a specific plate from the LPR cameras of the vehicle owner's zones.
 *
 * Needed wherever a plate stops being valid without a normal revoke:
 *   - the plate is EDITED — the camera keeps the old value alongside the new one, so the old
 *     plate goes on opening the gate forever;
 *   - the vehicle is DELETED — nothing else would ever tell the camera.
 *
 * Takes the plate explicitly rather than reading vehicle.plate, because in the edit case the
 * vehicle already carries the NEW plate by the time this runs.
 */
export async function revokeVehiclePlateFromLpr(
  customerId: string,
  vehicle: { id: string; person_id?: string | null; unit_id?: string | null },
  plateToRemove: string,
): Promise<void> {
  if (!plateToRemove || !plateToRemove.trim()) return;

  const personId = await resolveVehiclePersonId(customerId, vehicle);

  let lprCameraIds: string[] = [];
  if (personId) {
    ({ lprCameraIds } = await fetchPersonVehicleDeviceIds(customerId, personId));
  } else if (vehicle.unit_id) {
    ({ lprCameraIds } = await fetchUnitVehicleDeviceIds(customerId, vehicle.unit_id));
  }

  if (lprCameraIds.length === 0) return;

  const result = await syncPlateToLprCameras(
    customerId,
    lprCameraIds,
    { plate: plateToRemove },
    false,
  );

  // A stale plate left on a camera is a physical access hole — never fail silently.
  const warning = describeLprSyncFailures(result);
  if (warning) toast.error(warning);
}

export interface LprBulkProgress {
  done: number;
  total: number;
  ok: number;
  fail: number;
  label: string;
  status: 'ok' | 'failed';
}

export interface LprBulkResult {
  total: number;
  ok: number;
  fail: number;
  /** True when nothing was attempted because plates can only be written from the desktop app. */
  skippedNoDesktopApp?: boolean;
}

/**
 * Pushes every vehicle plate of a customer to the LPR cameras that vehicle is authorized for.
 *
 * The bulk "Sincronizar RFID de Veículos" action walks rfid_reader / controlid_rfid devices and
 * calls a per-device endpoint that knows nothing about plates, so LPR cameras were skipped
 * entirely while the run still reported success. This is the plate-side counterpart.
 *
 * Vehicle-centric rather than camera-centric because authorization is per vehicle (its owner's
 * zones decide which cameras apply), and because plates are written one at a time.
 *
 * `total` counts only vehicles that actually had somewhere to go — a vehicle whose zones
 * contain no camera is skipped, not failed.
 */
export async function syncAllVehiclePlatesToLpr(
  customerId: string,
  opts: { onProgress?: (p: LprBulkProgress) => void; shouldStop?: () => boolean } = {},
): Promise<LprBulkResult> {
  // Plates are written over the LAN by the desktop app. In a browser every camera call would
  // fail, turning a bulk run into one failure per vehicle for something that could never have
  // worked — so skip up front and let the caller explain it once.
  const inElectron = typeof window !== 'undefined' && !!(window as any).electronAPI?.deviceHttpRequest;
  if (!inElectron) {
    return { total: 0, ok: 0, fail: 0, skippedNoDesktopApp: true };
  }

  let vehicles: any[] = [];
  try {
    const resp = await fetchWithAuth(
      `/api/v1/customers/${customerId}/vehicles?page=1&page_size=5000`,
    );
    if (!resp.ok) return { total: 0, ok: 0, fail: 0 };
    const body = await resp.json();
    vehicles = body?.data ?? body?.vehicles ?? [];
  } catch {
    return { total: 0, ok: 0, fail: 0 };
  }

  const withPlate = vehicles.filter(v => (v.plate || '').trim());

  let ok = 0;
  let fail = 0;
  let done = 0;

  for (const vehicle of withPlate) {
    if (opts.shouldStop?.()) break;

    const personId = await resolveVehiclePersonId(customerId, vehicle);
    let lprCameraIds: string[] = [];
    if (personId) {
      ({ lprCameraIds } = await fetchPersonVehicleDeviceIds(customerId, personId));
    } else if (vehicle.unit_id) {
      ({ lprCameraIds } = await fetchUnitVehicleDeviceIds(customerId, vehicle.unit_id));
    }

    // Nothing to do for this vehicle — not a failure, and not counted in the total.
    if (lprCameraIds.length === 0) continue;

    const ownerName = vehicle.person
      ? `${vehicle.person.first_name || ''} ${vehicle.person.last_name || ''}`.trim()
      : (vehicle.owner_name || '');

    const result = await syncPlateToLprCameras(
      customerId,
      lprCameraIds,
      { plate: vehicle.plate, ownerName },
      true,
    );

    const succeeded = result.failed.length === 0;
    if (succeeded) ok++; else fail++;
    done++;

    opts.onProgress?.({
      done,
      total: withPlate.length,
      ok,
      fail,
      label: vehicle.plate,
      status: succeeded ? 'ok' : 'failed',
    });
  }

  return { total: ok + fail, ok, fail };
}

/**
 * Revokes a vehicle's PREVIOUS RFID tag from the readers and Guarita after the tag is edited.
 *
 * Without this the old card stays registered on every device and goes on opening the gate —
 * the tag-side twin of the stale-plate problem revokeVehiclePlateFromLpr solves.
 *
 * Deliberately clears `plate` before delegating: a tag-only edit leaves the plate unchanged and
 * still authorized, so the LPR cameras must not be touched. syncVehicleRfid skips LPR entirely
 * when there is no plate, which is exactly the behaviour wanted here.
 */
export async function revokeVehicleTagFromDevices(
  customerId: string,
  vehicle: SyncVehicleOptions['vehicle'],
  previousTag: string,
): Promise<void> {
  const tag = (previousTag || '').trim();
  if (!tag) return;

  await syncVehicleRfid({
    customerId,
    vehicle: { ...vehicle, rfid_tag: tag, plate: undefined },
    isAllowed: false,
  });
}

/**
 * Builds, for every LPR camera, the set of plates Panther authorizes on it.
 *
 * One pass over the customer's vehicles: each vehicle's owner zones decide which cameras apply,
 * so the mapping is produced vehicle-first and inverted here rather than asking each camera
 * "who may pass you", which the data model cannot answer directly.
 *
 * THROWS when the vehicle list cannot be read. That matters: reconciliation treats an empty
 * expectation as "this camera should hold nothing", and returning an empty map on a failed
 * lookup would present a whole gate's list as unauthorized.
 */
export async function buildExpectedLprPlates(customerId: string): Promise<Map<string, Set<string>>> {
  const resp = await fetchWithAuth(
    `/api/v1/customers/${customerId}/vehicles?page=1&page_size=5000`,
  );
  if (!resp.ok) {
    throw new Error('Não foi possível carregar os veículos para conferir as câmeras LPR');
  }
  const body = await resp.json();
  const vehicles: any[] = body?.data ?? body?.vehicles ?? [];

  const expected = new Map<string, Set<string>>();

  for (const vehicle of vehicles) {
    const plate = normalizePlate(vehicle.plate || '');
    if (!plate) continue;

    const personId = await resolveVehiclePersonId(customerId, vehicle);
    let lprCameraIds: string[] = [];
    if (personId) {
      ({ lprCameraIds } = await fetchPersonVehicleDeviceIds(customerId, personId));
    } else if (vehicle.unit_id) {
      ({ lprCameraIds } = await fetchUnitVehicleDeviceIds(customerId, vehicle.unit_id));
    }

    for (const cameraId of lprCameraIds) {
      const set = expected.get(cameraId) ?? new Set<string>();
      set.add(plate);
      expected.set(cameraId, set);
    }
  }

  return expected;
}
